import { Component, DestroyRef, Input, OnChanges, OnInit, Output, EventEmitter, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonNoteViewModel, ComponentValidationResult, ComponentViewModel, ComponentsInPipeClassViewModel } from 'src/app/shared/models/autogenerated-piping';
import { Observable } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { faExclamationTriangle, faTrashCan, faArrowsRotate, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { NpsValidationService } from 'src/app/shared/services/nps-validation.service';
import { RowOrderDirection } from 'src/app/shared/components/row-order-indicator/row-order-indicator.component';

/**
 * Per-row diff result for the components table in compare mode.
 * Rows are matched first by content fingerprint (description + nps + schRat + end) for move
 * detection, then positionally within their type group as a fallback for field-level diffs.
 */
interface ComponentRowDiff {
  component: ComponentsInPipeClassViewModel;
  /** Matching row from the previous revision, or null when newly added (no fingerprint match). */
  previousComponent: ComponentsInPipeClassViewModel | null;
  /** Direction the row moved relative to its previous position within this group. */
  orderDirection: RowOrderDirection;
  /** True when this component code does not appear in the previous revision. */
  isNew: boolean;
  /** True for ghost rows: present in the previous revision but absent from the current group. */
  isDeleted: boolean;
}

@Component({
    selector: 'app-pipe-component-details',
    templateUrl: './pipe-component-details.component.html',
    styleUrls: ['./pipe-component-details.component.scss'],
    standalone: false
})
export class PipeComponentDetailsComponent implements OnChanges, OnInit{
  @Input()
  groupName: string;
  @Input()
  componentsInPipeClass: ComponentsInPipeClassViewModel[];
  @Input()
  currentRevisionName: string; // Current revision name for comparison
  @Input()
  isRevisionInProgress: boolean = false; // Whether the revision is being edited (not published)
  @Input()
  canEdit$: Observable<boolean>;
  @Output()
  editGroupNotesEvent = new EventEmitter<string>();
  @Output()
  editComponentNotesEvent = new EventEmitter<ComponentsInPipeClassViewModel>();
  @Output()
  addComponentsEvent = new EventEmitter<string>();
  @Output()
  addGroupNoteEvent = new EventEmitter<string>(); // New event for adding group note
  @Output()
  removeComponentEvent = new EventEmitter<ComponentsInPipeClassViewModel>(); // New event for removing component
  @Output()
  updateNpsEvent = new EventEmitter<{component: ComponentsInPipeClassViewModel, newNps: string}>(); // New event for updating NPS
  @Output()
  updateSchRatEvent = new EventEmitter<{component: ComponentsInPipeClassViewModel, newSchRat: string}>();
  @Output()
  reorderComponentsEvent = new EventEmitter<{groupName: string, components: ComponentsInPipeClassViewModel[]}>(); // New event for reordering components

  @Input()
  isCompareMode: boolean = false;
  /** Components from the previous revision for this group, used to compute row-level diffs. */
  @Input()
  previousComponentsInPipeClass: ComponentsInPipeClassViewModel[] = [];
  /** Catalog validation results keyed by ComponentViewModel.componentId, passed from piping-tabs-parent. */
  @Input()
  componentValidationResults: Map<string, ComponentValidationResult> = new Map();
  /** Emitted when the user requests replacing a component with its latest catalog version. */
  @Output()
  replaceComponentEvent = new EventEmitter<{ current: ComponentsInPipeClassViewModel; latestVersion: ComponentViewModel }>();

  notesMutualForGroup: CommonNoteViewModel[];

  /**
   * Precomputed row diffs for the current component list.
   * Always populated (even outside compare mode) so the template uses a single *ngFor.
   */
  componentRowDiffs: ComponentRowDiff[] = [];

  /** Components in the previous revision that are absent from the current list.
   *  Rendered as deleted (red) ghost rows at the end of the group in compare mode. */
  deletedComponentRows: ComponentsInPipeClassViewModel[] = [];

  /** Group-level notes from the previous revision; used to exclude them from per-row diffs. */
  previousNotesMutualForGroup: CommonNoteViewModel[] = [];

  /** Note codes for the current group (for group-row diff in compare mode). */
  get currentGroupNoteCodes(): string[] {
    return (this.notesMutualForGroup || []).map(n => n.codeId);
  }

  /** Note codes for the previous group (for group-row diff in compare mode). */
  get previousGroupNoteCodes(): string[] {
    return this.previousNotesMutualForGroup.map(n => n.codeId);
  }

  // Properties for NPS editing
  editingNpsComponent: ComponentsInPipeClassViewModel | null = null;
  editingNpsValue: string = '';
  editingSchRatComponent: ComponentsInPipeClassViewModel | null = null;
  editingSchRatValue: string = '';

  expanded = true;
  faTrashCan = faTrashCan;
  faArrowsRotate = faArrowsRotate;
  faExclamationTriangle = faExclamationTriangle;
  /** Shown as a passive orange badge when the component is outdated but the user cannot edit. */
  faCircleExclamation = faCircleExclamation;

  private readonly destroyRef = inject(DestroyRef);
  /** Synchronous snapshot of canEdit$. Updated on every emission so click guards don't need the async pipe. */
  isEditable: boolean = false;

  private readonly _npsValidationService = inject(NpsValidationService);

  constructor() {}

  ngOnInit(): void {
    // Track canEdit$ as a plain boolean so the upgrade click handler can guard synchronously,
    // and so the aria-disabled attribute can be set without a second async pipe evaluation.
    this.canEdit$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => { this.isEditable = v; });
    this.notesMutualForGroup = this.getNotesMutualForGroup(this.componentsInPipeClass);
    this.sortCommonNotes();
    this.computeComponentDiff();
  }

  ngOnChanges(): void {
    this.sortComponents();

    // IMPORTANT: Recalculate mutual notes when component data changes
    if (this.componentsInPipeClass) {

      this.notesMutualForGroup = this.getNotesMutualForGroup(this.componentsInPipeClass);
      this.sortCommonNotes();
      // Validate and sync group notes automatically
      this.validateGroupNotes();
    }
    this.computeComponentDiff();
  }

  sortComponents() {
    // Sort by displayOrder first, then by component type
    this.componentsInPipeClass.sort((a, b) => {
      // Primary sort: displayOrder
      if (a.displayOrder !== b.displayOrder) {
        return a.displayOrder - b.displayOrder;
      }

      // Secondary sort: component type
      const typeA = a.component?.type || '';
      const typeB = b.component?.type || '';
      return typeA.localeCompare(typeB);
    });
  }

  sortCommonNotes() {
    this.notesMutualForGroup.sort((a, b) => Number(a.codeId) - Number(b.codeId));
  }

  getJoinedCommonNoteCodes(componentInPipeClass: ComponentsInPipeClassViewModel): string {
    if(componentInPipeClass.commonNotes && componentInPipeClass.commonNotes.length > 0) {
          return componentInPipeClass.commonNotes.map(commonNote => commonNote.codeId).join(", ");
    }
    return "";
  }

  getNotesMutualForGroup(components: ComponentsInPipeClassViewModel[]): CommonNoteViewModel[] {
    // Handle empty or single component case
    if (!components || components.length === 0) {
      return [];
    }

    if (components.length === 1) {
      // Single component - no mutual notes
      return [];
    }

    const counterMap = new Map<string, { note: CommonNoteViewModel, counter: number}>();

    components.forEach(component => {
      if (component.commonNotes && component.commonNotes.length > 0) {
        component.commonNotes.forEach(note => {
          const noteAndCounter = counterMap.get(note.codeId);
          let counter = noteAndCounter?.counter ?? 0;
          counterMap.set(note.codeId, {note: note, counter: ++counter});
        });
      }
    });

    let mutualNotes = [];
    for(let [key, value] of counterMap){
      // Only include notes that are present in ALL components
      if(value.counter === components.length) {
        mutualNotes.push(value.note);
      }
    }

    return mutualNotes;
  }

  /**
   * Validate that group notes are properly synchronized
   * This method ensures data integrity between individual and group notes
   */
  validateGroupNotes(): void {
    if (!this.componentsInPipeClass || this.componentsInPipeClass.length <= 1) {
      return;
    }

    // Check if there are notes that should be promoted to group level
    const actualMutualNotes = this.getNotesMutualForGroup(this.componentsInPipeClass);

    // Log validation results for debugging
    if (actualMutualNotes.length !== this.notesMutualForGroup.length) {
      console.warn(`Group notes validation: Expected ${actualMutualNotes.length} mutual notes, found ${this.notesMutualForGroup.length}`, {
        group: this.groupName,
        expected: actualMutualNotes.map(n => n.codeId),
        current: this.notesMutualForGroup.map(n => n.codeId)
      });
    }
  }

  excludeMutualNotes(notes: Array<CommonNoteViewModel>): Array<CommonNoteViewModel> {
    if (!notes || notes.length === 0) {
      return [];
    }

    if (!this.notesMutualForGroup || this.notesMutualForGroup.length === 0) {
      return notes.sort((a, b) => Number(a.codeId) - Number(b.codeId));
    }

    const mutualNoteCodes = new Set(this.notesMutualForGroup.map(note => note.codeId));

    return notes
      .filter(note => !mutualNoteCodes.has(note.codeId))
      .sort((a, b) => Number(a.codeId) - Number(b.codeId));
  }

  /** TrackBy for componentRowDiffs to preserve DOM identity across change detection cycles. */
  trackByComponent(_index: number, row: ComponentRowDiff): ComponentsInPipeClassViewModel {
    return row.component;
  }

  // ── Compare mode helpers ──────────────────────────────────────────────────

  /**
   * Builds a stable fingerprint for a component row, used in Phase 1 of the compare diff
   * to detect rows that are identical in content but have moved to a different position.
   *
   * The fingerprint combines the catalog-driven description with the per-pipe-class override
   * fields (nps, schRat, end). Rows whose fingerprint matches across revisions are treated as
   * the same logical component; a position change means the row was reordered, not replaced.
   *
   * Note: `component.type` is not included because matching already occurs within a type group.
   */
  private buildComponentFingerprint(comp: ComponentsInPipeClassViewModel): string {
    const c = comp.component;
    return [c?.description ?? '', c?.nps ?? '', c?.schRat ?? '', c?.end ?? ''].join('||');
  }

  /**
   * Builds `componentRowDiffs`, `deletedComponentRows`, and `previousNotesMutualForGroup`
   * from the current and previous component lists for this group.
   *
   * **Two-phase matching strategy:**
   *
   * *Phase 1 — Fingerprint matching (move detection):*
   * Each current row is matched to a previous row of the same type whose `(description, nps,
   * schRat, end)` fingerprint is identical. When a fingerprint match is found but the row's
   * index within its type group changed, `orderDirection` is set to `'up'` or `'down'` and the
   * row is rendered with a directional arrow rather than field-level diffs (since all diffed
   * fields are part of the fingerprint and are therefore unchanged).
   *
   * *Phase 2 — Positional fallback (field-level diffs):*
   * Rows that were not matched in Phase 1 are matched positionally within their type group,
   * preserving the original full-replacement rule: if the positionally-matched previous row has
   * a different `description`, both are treated as a catalog-entry replacement and the current
   * row is shown as new (green) while the previous appears as a deleted (red) ghost row.
   *
   * **Why two phases?**
   * Purely positional matching (old behaviour) fails for row swaps: two rows with identical
   * content that swapped positions are shown as field changes on every column. Fingerprint
   * matching resolves this by identifying moves first, leaving only genuine value edits for
   * Phase 2. Matching by `component.code` is the intended future strategy once codes are
   * confirmed to be stable and unique across revisions.
   *
   * Called in both `ngOnInit` and `ngOnChanges` to stay in sync with input changes.
   */
  private computeComponentDiff(): void {
    // Sort by displayOrder so index-based direction computation matches the rendered row order.
    const currentList: ComponentsInPipeClassViewModel[] = [...(this.componentsInPipeClass || [])]
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const prevList: ComponentsInPipeClassViewModel[] = [...(this.previousComponentsInPipeClass || [])]
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    // Pre-compute previous group-level mutual notes so they can be excluded from per-row diffs.
    this.previousNotesMutualForGroup = (this.isCompareMode && prevList.length > 0)
      ? this.getNotesMutualForGroup(prevList)
      : [];

    if (!this.isCompareMode || prevList.length === 0) {
      this.componentRowDiffs = currentList.map(component => ({
        component,
        previousComponent: null,
        orderDirection: 'unchanged' as RowOrderDirection,
        isNew: false,
        isDeleted: false
      }));
      this.deletedComponentRows = [];
      return;
    }

    // ── Phase 1: Fingerprint matching ────────────────────────────────────────
    // Build a per-type pool of previous rows. Each pool is consumed greedily during matching
    // so duplicate fingerprints within a type are resolved positionally.
    const phase1AvailByType = new Map<string, ComponentsInPipeClassViewModel[]>();
    for (const comp of prevList) {
      const type: string = comp.component?.type ?? '';
      if (!phase1AvailByType.has(type)) { phase1AvailByType.set(type, []); }
      const pool: ComponentsInPipeClassViewModel[] | undefined = phase1AvailByType.get(type);
      if (pool) { pool.push(comp); }
    }

    const phase1MatchedCurrentRows = new Set<ComponentsInPipeClassViewModel>();
    const phase1MatchedPrevRows = new Set<ComponentsInPipeClassViewModel>();
    const phase1Diffs = new Map<ComponentsInPipeClassViewModel, ComponentRowDiff>();

    for (const comp of currentList) {
      const type: string = comp.component?.type ?? '';
      const available: ComponentsInPipeClassViewModel[] = phase1AvailByType.get(type) ?? [];
      const fingerprint: string = this.buildComponentFingerprint(comp);
      const matchIdx: number = available.findIndex(
        p => this.buildComponentFingerprint(p) === fingerprint
      );

      if (matchIdx < 0) { continue; } // No fingerprint match — handled in Phase 2.

      const prevRow: ComponentsInPipeClassViewModel = available.splice(matchIdx, 1)[0];
      phase1MatchedCurrentRows.add(comp);
      phase1MatchedPrevRows.add(prevRow);

      // Use the row's position in the full sorted list (across ALL types in the group) rather
      // than its within-type index. Within-type indices never change when rows from different
      // types are interleaved (e.g. [Nipple, Nipple, Swage] → [Nipple, Swage, Nipple]) — only
      // the full-list index reflects the actual visual reordering the user performed.
      const currentIndexInFullList: number = currentList.indexOf(comp);
      const prevIndexInFullList: number = prevList.indexOf(prevRow);

      let orderDirection: RowOrderDirection = 'unchanged';
      if (currentIndexInFullList < prevIndexInFullList) { orderDirection = 'up'; }
      else if (currentIndexInFullList > prevIndexInFullList) { orderDirection = 'down'; }

      phase1Diffs.set(comp, {
        component: comp,
        previousComponent: prevRow,
        orderDirection,
        isNew: false,
        isDeleted: false
      });
    }

    // ── Phase 2: Positional fallback for unmatched rows ───────────────────────
    // Rows that were not fingerprint-matched are matched positionally within their type,
    // preserving the original full-replacement rule for description mismatches.
    const unmatchedCurrent: ComponentsInPipeClassViewModel[] = currentList.filter(
      c => !phase1MatchedCurrentRows.has(c)
    );
    const unmatchedPrev: ComponentsInPipeClassViewModel[] = prevList.filter(
      p => !phase1MatchedPrevRows.has(p)
    );

    const unmatchedPrevByType = new Map<string, ComponentsInPipeClassViewModel[]>();
    for (const comp of unmatchedPrev) {
      const type: string = comp.component?.type ?? '';
      if (!unmatchedPrevByType.has(type)) { unmatchedPrevByType.set(type, []); }
      const unmatchedTypeEntries: ComponentsInPipeClassViewModel[] | undefined = unmatchedPrevByType.get(type);
      if (unmatchedTypeEntries) { unmatchedTypeEntries.push(comp); }
    }

    const unmatchedPrevConsumedCount = new Map<string, number>();
    // Previous rows that were positionally matched but treated as replacements (description
    // differed → different catalog entry → show as deleted ghost).
    const phase2ReplacedPrevRows = new Set<ComponentsInPipeClassViewModel>();

    const phase2Diffs: ComponentRowDiff[] = unmatchedCurrent.map((comp: ComponentsInPipeClassViewModel) => {
      const type: string = comp.component?.type ?? '';
      const prevSameType: ComponentsInPipeClassViewModel[] = unmatchedPrevByType.get(type) ?? [];
      const usedCount: number = unmatchedPrevConsumedCount.get(type) ?? 0;
      unmatchedPrevConsumedCount.set(type, usedCount + 1);

      let previousComponent: ComponentsInPipeClassViewModel | null =
        usedCount < prevSameType.length ? prevSameType[usedCount] : null;

      // Full-replacement rule: description mismatch means a different catalog entry was selected.
      if (previousComponent !== null) {
        const currentDesc: string = comp.component?.description ?? '';
        const prevDesc: string = previousComponent.component?.description ?? '';
        if (currentDesc !== prevDesc) {
          phase2ReplacedPrevRows.add(previousComponent);
          previousComponent = null;
        }
      }

      return {
        component: comp,
        previousComponent,
        orderDirection: 'unchanged' as RowOrderDirection,
        isNew: previousComponent === null,
        isDeleted: false
      };
    });

    // Deleted rows: unmatched previous rows not consumed by Phase 2 positional matching,
    // plus rows that were consumed but rejected by the full-replacement rule.
    const deletedRowDiffs: ComponentRowDiff[] = unmatchedPrev
      .filter((pc: ComponentsInPipeClassViewModel) => {
        if (phase2ReplacedPrevRows.has(pc)) { return true; }
        const type: string = pc.component?.type ?? '';
        const prevSameType: ComponentsInPipeClassViewModel[] = unmatchedPrevByType.get(type) ?? [];
        return prevSameType.indexOf(pc) >= (unmatchedPrevConsumedCount.get(type) ?? 0);
      })
      .map(comp => ({
        component: comp,
        previousComponent: null,
        orderDirection: 'unchanged' as RowOrderDirection,
        isNew: false,
        isDeleted: true
      }));

    // Merge all diffs and sort by the current revision's displayOrder so rows appear in the
    // order the user sees them. Deleted ghost rows sort after current rows at the same position.
    this.componentRowDiffs = [...phase1Diffs.values(), ...phase2Diffs, ...deletedRowDiffs]
      .sort((a, b) => {
        const orderA: number = a.component.displayOrder ?? 0;
        const orderB: number = b.component.displayOrder ?? 0;
        if (orderA !== orderB) { return orderA - orderB; }
        // Same displayOrder: current rows before deleted rows.
        return (a.isDeleted ? 1 : 0) - (b.isDeleted ? 1 : 0);
      });
    this.deletedComponentRows = []; // merged into componentRowDiffs
  }

  /**
   * Returns the per-row note codes for display.
   *
   * In compare mode the group-header row is hidden, so mutual group notes must be included here
   * on every data row (otherwise they would be invisible entirely). This means a row that merely
   * moved positions will still show its group notes, but since the same notes exist on the
   * matching previous row the `app-revision-list-field` diff will produce no visible change.
   *
   * In normal (non-compare) mode mutual notes are excluded because they are shown on the group
   * header row instead.
   */
  getComponentNoteCodes(row: ComponentRowDiff): string[] {
    if (this.isCompareMode) { return (row.component.commonNotes || []).map(n => n.codeId ?? ''); }
    return this.excludeMutualNotes(row.component.commonNotes || []).map(n => n.codeId ?? '');
  }

  /**
   * Returns the per-row note codes for the previous component in a row diff.
   * Same all-notes-included logic as `getComponentNoteCodes` in compare mode — the previous
   * revision's group header is also hidden so all notes must come from the row itself.
   */
  getPreviousComponentNoteCodes(row: ComponentRowDiff): string[] {
    if (!row.previousComponent) { return []; }
    if (this.isCompareMode) { return (row.previousComponent.commonNotes || []).map(n => n.codeId ?? ''); }
    return this.excludePreviousGroupNotes(row.previousComponent.commonNotes || []).map(n => n.codeId ?? '');
  }

  /** @deprecated Deleted rows are now part of `componentRowDiffs`. Kept for template backward-compat. */
  getPreviousDeletedComponentNoteCodes(comp: ComponentsInPipeClassViewModel): string[] {
    return (comp.commonNotes || []).map(n => n.codeId);
  }

  /**
   * Filters out notes that were shared (mutual) across the previous revision's group components.
   * Mirrors `excludeMutualNotes` but operates on `previousNotesMutualForGroup`.
   */
  private excludePreviousGroupNotes(notes: CommonNoteViewModel[]): CommonNoteViewModel[] {
    const prevGroupCodes: Set<string> = new Set(this.previousNotesMutualForGroup.map(n => n.codeId));
    return notes
      .filter(n => !prevGroupCodes.has(n.codeId))
      .sort((a, b) => Number(a.codeId) - Number(b.codeId));
  }

  editGroupNotes(): void {
    this.editGroupNotesEvent.emit(this.groupName);
  }

  editComponentNotes(component: ComponentsInPipeClassViewModel): void {
    this.editComponentNotesEvent.emit(component);
  }

  addComponents(): void {
    this.addComponentsEvent.emit(this.groupName);
  }

  addGroupNote(): void {
    this.addGroupNoteEvent.emit(this.groupName);
  }

  removeComponent(component: ComponentsInPipeClassViewModel): void {
    this.removeComponentEvent.emit(component);
  }

  // ── Catalog validation helpers ───────────────────────────────────────────

  /**
   * Returns the validation result for a given row, or undefined if not yet validated.
   * Keyed by the inner ComponentViewModel.componentId.
   */
  getComponentValidationResult(component: ComponentsInPipeClassViewModel): ComponentValidationResult | undefined {
    const id: string | undefined = component.component?.componentId;
    if (!id) { return undefined; }
    return this.componentValidationResults.get(id);
  }

  /** True when the component is outdated and a replacement is available. */
  componentHasUpgrade(component: ComponentsInPipeClassViewModel): boolean {
    const result = this.getComponentValidationResult(component);
    return !!result && result.isCurrent === false && !!result.latestVersion;
  }

  /** True when the component is outdated and no replacement can be determined (deprecated). */
  componentIsDeprecated(component: ComponentsInPipeClassViewModel): boolean {
    const result = this.getComponentValidationResult(component);
    return !!result && result.isCurrent === false && !result.latestVersion;
  }

  /** Tooltip text for the upgrade/deprecated indicator button.
   * Appends "Click to update." when the user can edit and an upgrade is available. */
  getComponentValidationTooltip(component: ComponentsInPipeClassViewModel, canEdit: boolean | null = false): string {
    const result = this.getComponentValidationResult(component);
    if (!result || result.isCurrent) { return ''; }
    if (result.latestVersion) {
      const base: string = result.message || 'A newer version of this component is available.';
      return canEdit ? `${base}\nClick to update.` : base;
    }
    return result.message || 'This component is deactivated and no replacement is available.';
  }

  /** Called when the user clicks the upgrade indicator button. */
  onReplaceComponent(component: ComponentsInPipeClassViewModel): void {
    // isEditable guards against keyboard activation while aria-disabled is true.
    if (!this.isEditable) { return; }
    const result = this.getComponentValidationResult(component);
    if (!result?.latestVersion) { return; }
    this.replaceComponentEvent.emit({ current: component, latestVersion: result.latestVersion });
  }

  // NPS editing methods
  startEditingNps(component: ComponentsInPipeClassViewModel): void {
    this.editingNpsComponent = component;
    this.editingNpsValue = component.component.nps || '';
  }

  cancelEditingNps(): void {
    this.editingNpsComponent = null;
    this.editingNpsValue = '';
  }

  saveNps(): void {
    if (this.editingNpsComponent) {
      // Trim whitespace from the value
      const trimmedValue = this.editingNpsValue.trim();

      // Update the component's NPS value locally
      this.editingNpsComponent.component.nps = trimmedValue;

      // Derive npsFrom/npsTo so all three fields are in sync when the full PUT is sent.
      // Reducers (nps containing 'x') are skipped — UX to be defined with the SME.
      const parsedRange = this._npsValidationService.parseNpsRange(trimmedValue);
      if (parsedRange) {
        this.editingNpsComponent.component.npsFrom = parsedRange.npsFrom;
        this.editingNpsComponent.component.npsTo = parsedRange.npsTo;
      }

      // Emit the update event so parent can handle persistence
      this.updateNpsEvent.emit({
        component: this.editingNpsComponent,
        newNps: trimmedValue
      });

      // Re-sort components to reflect changes
      this.sortComponents();

      // Clear editing state
      this.cancelEditingNps();
    }
  }

  isEditingNps(component: ComponentsInPipeClassViewModel): boolean {
    return this.editingNpsComponent === component;
  }

  // SCH/RAT editing methods
  startEditingSchRat(component: ComponentsInPipeClassViewModel): void {
    this.editingSchRatComponent = component;
    this.editingSchRatValue = component.component.schRat || '';
  }

  cancelEditingSchRat(): void {
    this.editingSchRatComponent = null;
    this.editingSchRatValue = '';
  }

  saveSchRat(): void {
    if (this.editingSchRatComponent) {
      const trimmedValue = this.editingSchRatValue.trim();
      this.editingSchRatComponent.component.schRat = trimmedValue;
      this.updateSchRatEvent.emit({
        component: this.editingSchRatComponent,
        newSchRat: trimmedValue
      });
      this.cancelEditingSchRat();
    }
  }

  isEditingSchRat(component: ComponentsInPipeClassViewModel): boolean {
    return this.editingSchRatComponent === component;
  }

  // Drag and drop methods
  onComponentDrop(event: CdkDragDrop<ComponentsInPipeClassViewModel[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      // Update the local array order
      moveItemInArray(this.componentsInPipeClass, event.previousIndex, event.currentIndex);

      // Update display orders to maintain the new sequence
      this.componentsInPipeClass.forEach((component, index) => {
        component.displayOrder = index;
      });

      // Emit the reorder event so parent can handle persistence
      this.reorderComponentsEvent.emit({
        groupName: this.groupName,
        components: this.componentsInPipeClass
      });
    }
  }
  getLastRevisionSegment(component: ComponentsInPipeClassViewModel): string {
    const isNew: boolean = (component as any).__isNew;
    const isModified: boolean = (component as any).__isModified;
    const componentRevision: string = component.lastUpdatedRevision || '';

    if (this.isRevisionInProgress) {
      // Revision is in progress: only show if this item was changed in the current revision.
      // Use the last hyphen-segment for comparison because lastUpdatedRevision stores the short
      // Revision.Name ("1") while currentRevisionName uses the ReleaseFullName ("PIM-SU-5112-1").
      // Both end in the same short label, so comparing last segments handles both formats.
      const lastSegment = (s: string): string => s.split('-').pop() || '';
      const belongsToCurrentRevision: boolean = lastSegment(componentRevision) === lastSegment(this.currentRevisionName);
      if (!isNew && !isModified && !belongsToCurrentRevision) {
        return '';
      }
    }

    // Published/viewing mode: show lastUpdatedRevision if available
    const revision: string = componentRevision;
    if (!revision || revision.trim() === '') {
      return '';
    }

    const segments: string[] = revision.split('-');
    return segments[segments.length - 1] || '';
  }
}
